import React, { useState, useEffect } from "react";
import { FaCode, FaTimes, FaPlay, FaExclamationTriangle, FaClock, FaTrophy } from "react-icons/fa";
import { toast } from "react-toastify";
import CONFIG from "../config";
import "./AmaliyModal.css";

function AmaliyModal({ mavzuId, taskData, history = [], lang = "html", onClose, onPass }) {
    const [code, setCode] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [seconds, setSeconds] = useState(0);
    const [isClosing, setIsClosing] = useState(false);
    const highlighterRef = React.useRef(null);

    // Sync scrolling
    const handleScroll = (e) => {
        if (highlighterRef.current) {
            highlighterRef.current.scrollTop = e.target.scrollTop;
            highlighterRef.current.scrollLeft = e.target.scrollLeft;
        }
    };

    useEffect(() => {
        const timer = setInterval(() => {
            setSeconds(prev => prev + 1);
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    const formatTime = (secs) => {
        const m = Math.floor(secs / 60);
        const s = secs % 60;
        return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    };

    const handleSubmit = async () => {
        if (code.trim().length < 10) {
            toast.warning("Iltimos, avval kodni yozing!");
            return;
        }

        setIsSubmitting(true);
        try {
            const token = localStorage.getItem("loyiha_token");
            const res = await fetch(`${CONFIG.API_BASE_URL}/submit_practical.php?token=${token}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    mavzu_id: mavzuId,
                    code: code,
                    time_spent: seconds
                })
            }).then(r => r.json());

            if (res.status === 'success') {
                if (res.is_passed) {
                    toast.success(`Barakalla! Topshiriq ${formatTime(seconds)} vaqtda bajarildi.`);
                    if (onPass) onPass();
                    handleClose();
                } else {
                    toast.error(res.message || "Xatolik: Shartlar to'liq bajarilmadi.");
                }
            } else {
                toast.error(res.message);
            }
        } catch (err) {
            toast.error("Natijani yuborishda xatolik yuz berdi");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        toast.error("Nusxa ko'chirib joylash taqiqlangan! Iltimos, kodni o'zingiz yozing.");
    };

    // Calculate best record
    const bestRecord = history.length > 0
        ? [...history].sort((a, b) => a.vaqt - b.vaqt)[0]
        : null;

    const highlightCode = (code, language) => {
        if (!code) return "";
        let escaped = code
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;");

        if (language === "html") {
            // First find tags, then highlight parts inside them to avoid double matching
            return escaped.replace(/(&lt;\/?[a-z1-6]+)(.*?)(&gt;)/gi, (match, opening, content, closing) => {
                const highlightedContent = content.replace(/([a-z-]+)=(['"].*?['"])/gi, (m, attr, val) => {
                    return ` <span class="code-attr">${attr}</span>=<span class="code-string">${val}</span>`;
                });
                return `<span class="code-tag">${opening}</span>${highlightedContent}<span class="code-tag">${closing}</span>`;
            });
        }

        // Keywords for JS, PHP, Python
        const keywords = {
            js: ["const", "let", "var", "function", "return", "if", "else", "for", "while", "import", "export", "class", "new", "async", "await", "try", "catch"],
            php: ["php", "echo", "if", "else", "foreach", "for", "while", "function", "return", "class", "public", "private", "protected", "new", "namespace", "use"],
            python: ["def", "class", "if", "else", "elif", "for", "while", "import", "from", "return", "print", "in", "as", "try", "except", "with"]
        };

        const currentKeywords = keywords[language] || [];

        // Strings
        escaped = escaped.replace(/(['"].*?['"])/g, '<span class="code-string">$1</span>');

        // Keywords (Ensure not inside tags or strings - simplistic but effective for common code)
        if (currentKeywords.length > 0) {
            const pattern = new RegExp(`\\b(${currentKeywords.join("|")})\\b`, "g");
            escaped = escaped.replace(pattern, (match) => {
                // If it's already inside a span (string), don't color it
                return `<span class="code-keyword">${match}</span>`;
            });
        }

        // Comments
        escaped = escaped.replace(/(\/\/[^\n]*|#[^\n]*)/g, '<span class="code-comment">$1</span>');

        return escaped;
    };

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            onClose();
            setIsClosing(false);
        }, 300);
    };

    return (
        <div className={`amaliy-modal-overlay ${isClosing ? 'modal-overlay-fade-out' : 'modal-overlay-fade-in'}`}>
            <div className={`amaliy-modal-card ${isClosing ? 'modal-content-pop-out' : 'modal-content-pop-in'}`}>
                <div className="amaliy-header">
                    <h3><FaCode /> Amaliy Topshiriq</h3>
                    <div className="amaliy-timer">
                        <FaClock /> {formatTime(seconds)}
                    </div>
                    <button className="close-amaliy-btn" onClick={handleClose}>
                        <FaTimes />
                    </button>
                </div>
                <div className="amaliy-body">
                    <div className="instruction-box">
                        <h4>Vazifa:</h4>
                        <p>{taskData?.instruction || "Ushbu mavzu bo'yicha amaliy topshiriqni bajaring."}</p>
                    </div>

                    <div className="editor-container">
                        <div className="editor-header">
                            <span>index.{lang === 'python' ? 'py' : lang === 'php' ? 'php' : lang === 'js' ? 'js' : 'html'}</span>
                            <span style={{ color: '#61afef' }}>{lang.toUpperCase()}</span>
                        </div>
                        <div className="code-editor-wrapper">
                            <pre className="code-highlighter" ref={highlighterRef} aria-hidden="true">
                                <code dangerouslySetInnerHTML={{ __html: highlightCode(code, lang) + "\n" }}></code>
                            </pre>
                            <textarea
                                className="code-textarea"
                                placeholder="Kodingizni shu yerga yozing..."
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                                onScroll={handleScroll}
                                onPaste={handlePaste}
                                onContextMenu={(e) => e.preventDefault()}
                                spellCheck="false"
                                autoComplete="off"
                                autoCorrect="off"
                                autoCapitalize="off"
                                autoFocus
                            ></textarea>
                        </div>
                    </div>
                </div>
                <div className="amaliy-footer">
                    <div className="footer-left">
                        <div className="no-copy-hint">
                            <FaExclamationTriangle color="#e67e22" />
                            Nusxa olish va joylash taqiqlangan
                        </div>

                        {history && history.length > 0 && bestRecord && (
                            <div className="amaliy-record-container">
                                <div className="best-record-badge">
                                    <FaTrophy className="record-icon" />
                                    <span>Rekord: {formatTime(bestRecord.vaqt)}</span>
                                </div>
                                <div className="history-hover-popup">
                                    <h4>Urinishlar tarixi ({history.length})</h4>
                                    <div className="hover-list">
                                        {history.map((h, i) => (
                                            <div key={i} className="hover-item">
                                                <span className="h-num">#{h.urinish}</span>
                                                <span className="h-time">{formatTime(h.vaqt)}</span>
                                                <span className="h-date">{new Date(h.sana).toLocaleDateString()}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    <button
                        className="submit-amaliy-btn"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? "Tekshirilmoqda..." : <><FaPlay /> Tekshirish</>}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default AmaliyModal;
